/**
 * cart-special-treats.js
 * ─────────────────────────────────────────────────────────────────
 * Special treats carousel + threshold-based auto-removal logic.
 * Depends on: cart-shared.js  (isWidgetEnabled, plumGetSharedCart,
 *                               plumBustSharedCart, plumRefreshCart)
 * ─────────────────────────────────────────────────────────────────
 */
(function () {
  if (!isWidgetEnabled('show_recommendations')) return;

  // ─── Config helpers ────────────────────────────────────────────────────────

  function getSpecialTreatGroups(section) {
    const sectionId = section.dataset.sectionId;
    if (!sectionId) return [];

    const configEl = document.getElementById('SpecialTreatConfig-' + sectionId);
    if (!configEl) return [];

    try {
      const data = JSON.parse(configEl.textContent);
      return Array.isArray(data.groups) ? data.groups : [];
    } catch (error) {
      console.error('[special-treats] Failed to parse SpecialTreatConfig JSON:', error);
      return [];
    }
  }

  function isVariantInGroup(group, variantId) {
    if (!group || !group.products) return false;
    return group.products[String(variantId)] != null || group.products[variantId] != null;
  }

  function getCart() {
    return new Promise(function (resolve) {
      window.plumGetSharedCart(function (cart) { resolve(cart); });
    });
  }

  // ─── Line item removal ────────────────────────────────────────────────────

  async function removeLineItem(lineKey) {
    window.plumBustSharedCart();
    const response = await fetch('/cart/change.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ id: lineKey, quantity: 0 })
    });
    if (!response.ok) throw new Error('Failed to remove line item: ' + lineKey);
    return response.json();
  }

  // ─── Validation ───────────────────────────────────────────────────────────

  async function validateSpecialTreats(section) {
    const groups = getSpecialTreatGroups(section);
    if (!groups.length) return;

    const cart = await getCart();
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

    const specialLineItems = [];
    let baseSubtotal       = Number(cart.items_subtotal_price || 0);

    cart.items.forEach(function (item) {
      groups.forEach(function (group, groupIndex) {
        if (!isVariantInGroup(group, item.variant_id)) return;
        specialLineItems.push({
          item,
          groupIndex,
          threshold: Number(group.threshold || 0)
        });
        baseSubtotal -= Number(item.final_line_price || 0);
      });
    });

    if (!specialLineItems.length) return;

    const lineKeysToRemove = [];

    specialLineItems.forEach(function (entry) {
      if (lineKeysToRemove.includes(entry.item.key)) return;

      let subtotalForThreshold = baseSubtotal;

      specialLineItems.forEach(function (otherEntry) {
        if (otherEntry.groupIndex >= entry.groupIndex)        return;
        if (lineKeysToRemove.includes(otherEntry.item.key))  return;
        subtotalForThreshold += Number(otherEntry.item.final_line_price || 0);
      });

      if (entry.threshold > 0 && subtotalForThreshold < entry.threshold) {
        lineKeysToRemove.push(entry.item.key);
      }
    });

    const uniqueKeys = Array.from(new Set(lineKeysToRemove));
    if (!uniqueKeys.length) return;

    let latestCart = null;
    for (const lineKey of uniqueKeys) {
      latestCart = await removeLineItem(lineKey);
    }

    window.plumRefreshCart(latestCart);
  }

  // ─── Carousel ─────────────────────────────────────────────────────────────

  function initCartDrawerCarousel(section) {
    const track   = section.querySelector('.scroller__inner');
    const items   = section.querySelectorAll('.offer_items');
    const prevBtn = section.querySelector('.slider-nav__btn[name="prev"]');
    const nextBtn = section.querySelector('.slider-nav__btn[name="next"]');

    if (!track || !items.length) return;

    let currentIndex = 0;
    let startX       = 0;
    let dragOffset   = 0;
    let isDragging   = false;

    function getItemWidth() {
      const gap = 12;
      return (track.offsetWidth / 1.5) - (gap / 1.5);
    }

    function getMaxIndex() {
      return Math.max(0, items.length - 1);
    }

    function applyTranslate(offset) {
      track.style.transform = 'translateX(' + offset + 'px)';
    }

    function setActive(index) {
      currentIndex = Math.max(0, Math.min(index, getMaxIndex()));
      applyTranslate(-(currentIndex * (getItemWidth() + 12)));
      items.forEach(function (item, i) {
        item.classList.toggle('is-active', i === currentIndex);
      });
      if (prevBtn) prevBtn.toggleAttribute('disabled', currentIndex === 0);
      if (nextBtn) nextBtn.toggleAttribute('disabled', currentIndex === getMaxIndex());
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { setActive(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setActive(currentIndex + 1); });

    track.addEventListener('touchstart', function (e) {
      startX      = e.touches[0].clientX;
      dragOffset  = 0;
      isDragging  = true;
      track.style.transition = 'none';
    }, { passive: true });

    track.addEventListener('touchmove', function (e) {
      if (!isDragging) return;
      dragOffset = e.touches[0].clientX - startX;
      applyTranslate(-(currentIndex * (getItemWidth() + 12)) + dragOffset);
    }, { passive: true });

    track.addEventListener('touchend', function () {
      isDragging = false;
      track.style.transition = 'transform 0.35s ease';
      if (Math.abs(dragOffset) < 40) { setActive(currentIndex); return; }
      setActive(dragOffset < 0 ? currentIndex + 1 : currentIndex - 1);
    });

    let resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { setActive(currentIndex); }, 150);
    });

    setActive(0);
  }

  // ─── Boot & MutationObserver ──────────────────────────────────────────────

  function boot() {
    document.querySelectorAll('cart-drawer-special-treats').forEach(function (section) {
      if (!section.dataset.carouselInit) {
        section.dataset.carouselInit = 'true';
        initCartDrawerCarousel(section);
      }
    });
  }

  let validationTimer;

  function queueValidation() {
    clearTimeout(validationTimer);
    validationTimer = setTimeout(function () {
      document.querySelectorAll('cart-drawer-special-treats').forEach(function (section) {
        validateSpecialTreats(section).catch(function (error) {
          console.error('[special-treats] Validation failed:', error);
        });
      });
    }, 150);
  }

  const observer = new MutationObserver(function () {
    const uninitialized = document.querySelector('cart-drawer-special-treats:not([data-carousel-init])');
    if (uninitialized) boot();
    queueValidation();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('cart:updated', queueValidation);
  document.addEventListener('cart:refresh', queueValidation);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); queueValidation(); });
  } else {
    boot();
    queueValidation();
  }
})();
